import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  AlertTriangle, Building2, CheckCircle2, HelpCircle, Loader2, RotateCcw, Save, Settings,
  UploadCloud, X,
} from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import type {
  Item,
  QuoteApplyResponse, QuoteColMap, QuoteLine, QuoteParseResponse,
} from '@/types/models'
import { useAuth } from '@/stores/auth'
import { useCurrentCompany } from '@/lib/useCompany'
import { useToast } from '@/stores/toast'
import { QuoteTargetPicker } from '@/components/QuoteTargetPicker'
import type { QuoteTarget } from '@/components/QuoteTargetPicker'
import { ItemPicker } from '@/components/ItemPicker'
import { cn } from '@/lib/utils'

interface Row extends QuoteLine {
  skip: boolean
  save_mapping: boolean
}

type ColKey = 'code' | 'name' | 'spec' | 'unit' | 'price'
const COL_LABELS: Record<ColKey, string> = {
  code: '원본코드', name: '원본품명', spec: '규격', unit: '단위', price: '단가',
}

export function QuoteConvertTab() {
  const push = useToast((s) => s.push)
  const token = useAuth((s) => s.token)
  const company = useCurrentCompany()

  const [target, setTarget] = useState<QuoteTarget | null>(null)
  const [parsed, setParsed] = useState<QuoteParseResponse | null>(null)
  const [rows, setRows] = useState<Row[]>([])
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [effectiveTo, setEffectiveTo] = useState('')   // 비우면 무기한
  const [lastFile, setLastFile] = useState<File | null>(null)
  const [showMapper, setShowMapper] = useState(false)
  const [editSheet, setEditSheet] = useState('')
  const [editHeaderRow, setEditHeaderRow] = useState(0)
  const [editColMap, setEditColMap] = useState<QuoteColMap>({
    code: null, name: null, spec: null, unit: null, price: null,
  })
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [dragging, setDragging] = useState(false)
  // 방금 등록한 단가 배치(잘못 올렸을 때 원클릭 되돌리기용)
  const [undoInfo, setUndoInfo] = useState<{ group: number; from: string; count: number } | null>(null)
  const [undoing, setUndoing] = useState(false)

  const targetId = target?.id ?? null
  const canUpload = !!targetId

  // 견적서는 단일 파일만 허용 — 여러 개가 오면 거부하고 안내.
  function handleFiles(list: FileList | null) {
    if (!canUpload) { push('상위 거래처를 먼저 선택하세요', 'warn'); return }
    if (!list || !list.length) return
    if (list.length > 1) { push('견적서는 한 번에 한 개 파일만 올릴 수 있습니다', 'warn'); return }
    upload(list[0])
  }

  // 적용일이 정해지면 그 달 말일을 기본 종료일로 (월별 단가 워크플로우). 수정·비움 가능.
  function monthEndOf(iso: string): string {
    if (!iso) return ''
    const d = new Date(iso + 'T00:00:00')
    if (isNaN(d.getTime())) return ''
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
    const p = (n: number) => String(n).padStart(2, '0')
    return `${last.getFullYear()}-${p(last.getMonth() + 1)}-${p(last.getDate())}`
  }
  function setEffFrom(iso: string) {
    setEffectiveFrom(iso)
    setEffectiveTo(monthEndOf(iso))
  }

  function applyParseResponse(data: QuoteParseResponse) {
    setParsed(data)
    setEffFrom(data.effective_from || '')
    setRows(data.lines.map((l) => ({
      ...l,
      skip: false,
      // 매핑 저장 default:
      //   자동(code/name) → ON (기존 매핑 재확인)
      //   추정(guess)     → OFF (사용자가 확인 후 체크)
      //   미매칭          → OFF (저장할 item 자체가 없음)
      save_mapping: !!l.matched_item_id && l.matched_via !== 'guess',
    })))
    setEditSheet(data.sheet)
    setEditHeaderRow(data.header_row)
    setEditColMap(data.col_map)
    push(
      `파싱 완료 (${data.auto_detected ? '자동감지' : data.format_saved ? '저장된 포맷' : '수동'}): 총 ${data.summary.total}건 · 매핑 ${data.summary.matched} · 추정 ${data.summary.guessed} · 미매칭 ${data.summary.unmatched}`,
      'success', 5000,
    )
  }

  async function upload(file: File) {
    if (!targetId) { push('상위 거래처를 먼저 선택하세요', 'warn'); return }
    setLastFile(file)
    const fd = new FormData()
    fd.append('target_type', 'group')
    fd.append('target_id', String(targetId))
    fd.append('files', file)
    try {
      const res = await fetch(`${window.location.origin}/api/quote/parse`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(company ? { 'X-Company-Id': String(company.id) } : {}),
        },
        body: fd,
      })
      if (!res.ok) {
        const t = await res.text().catch(() => '')
        push(`업로드 실패: ${res.status} ${t}`, 'error', 6000)
        return
      }
      applyParseResponse(await res.json())
    } catch (e: any) {
      push(`업로드 오류: ${e.message}`, 'error', 5000)
    }
  }

  async function reparse() {
    if (!targetId || !lastFile) return
    const fd = new FormData()
    fd.append('target_type', 'group')
    fd.append('target_id', String(targetId))
    fd.append('files', lastFile)
    fd.append('sheet_hint', editSheet)
    fd.append('header_row', String(editHeaderRow))
    fd.append('col_map', JSON.stringify(editColMap))
    try {
      const res = await fetch(`${window.location.origin}/api/quote/reparse`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(company ? { 'X-Company-Id': String(company.id) } : {}),
        },
        body: fd,
      })
      if (!res.ok) { push(`재파싱 실패: ${res.status}`, 'error', 5000); return }
      applyParseResponse(await res.json())
    } catch (e: any) {
      push(`재파싱 오류: ${e.message}`, 'error', 5000)
    }
  }

  const saveFormat = useMutation({
    mutationFn: () => api('/api/quote/formats/', {
      method: 'POST',
      body: {
        group: targetId,
        sheet_hint: editSheet,
        header_row: editHeaderRow,
        col_map: editColMap,
        is_active: true,
      },
    }),
    onSuccess: () => push('포맷 저장됨. 다음 견적서부터 자동 적용.', 'success', 5000),
    onError: (e) => push(`포맷 저장 실패: ${e.message}`, 'error', 5000),
  })

  function updateItem(idx: number, it: Item) {
    // 사용자가 수동으로 변경 → matched_via 를 null 로 (=> "수동" 배지),
    // 매핑 저장 자동 ON (사용자가 확정한 것으로 간주)
    setRows((cur) => cur.map((r, i) =>
      i === idx
        ? {
            ...r,
            matched_item_id: it.id,
            matched_item_code: it.code,
            matched_item_name: it.name,
            matched_via: null,
            save_mapping: true,
          }
        : r
    ))
    // 자동으로 다음 미매칭 행으로 이동 (연쇄 워크플로우)
    focusNextTodo(idx)
  }

  const apply = useMutation({
    mutationFn: () => {
      if (!targetId || !effectiveFrom) throw new Error('상위 거래처와 적용일이 필요합니다')
      return api<QuoteApplyResponse>('/api/quote/apply', {
        method: 'POST',
        body: {
          target_type: 'group',
          target_id: targetId,
          effective_from: effectiveFrom,
          effective_to: effectiveTo || null,   // 비우면 무기한
          lines: rows
            .filter((r) => !r.skip && r.matched_item_id)
            .map((r) => ({
              source_code: r.source_code,
              source_name: r.source_name,
              item_id: r.matched_item_id,
              price: r.price,
              save_mapping: r.save_mapping,
            })),
        },
      })
    },
    onSuccess: (data) => {
      // 되돌리기 정보 저장(리셋 전에 targetId·effectiveFrom 캡처)
      const applied = data.created_prices + (data.updated_prices || 0)
      if (targetId && effectiveFrom && applied > 0) {
        setUndoInfo({ group: targetId, from: effectiveFrom, count: applied })
      }
      push(
        `단가 ${data.created_prices}건 등록${data.updated_prices ? ` / 0원→정상가 갱신 ${data.updated_prices}` : ''} (상위 → ${data.fanout_target_count}개 자식) / 중복 skip ${data.skipped_prices} / 매핑 신규 ${data.created_mappings} 수정 ${data.updated_mappings}`,
        'success', 10000,
      )
      setParsed(null); setRows([]); setEffectiveFrom(''); setEffectiveTo(''); setLastFile(null)
      if (fileRef.current) fileRef.current.value = ''
    },
    onError: (e) => push(`등록 실패: ${e.message}`, 'error', 6000),
  })

  // 방금 등록한 단가 배치를 되돌리기 (상위거래처+적용일 기준 일괄 삭제)
  async function undoLastApply() {
    if (!undoInfo) return
    const ok = await confirm(
      `방금 등록한 단가 ${undoInfo.count}건(적용일 ${undoInfo.from})을 삭제해 되돌릴까요?`,
      { danger: true },
    )
    if (!ok) return
    setUndoing(true)
    try {
      const res = await api<{ deleted: number }>('/api/prices/bulk-delete', {
        method: 'POST', body: { group: undoInfo.group, effective_from: undoInfo.from },
      })
      push(`단가 ${res.deleted}건 삭제 — 되돌리기 완료`, 'success')
      setUndoInfo(null)
    } catch (e: any) {
      push(e?.message || '되돌리기 실패', 'error')
    } finally {
      setUndoing(false)
    }
  }

  const readyToApply = rows.filter((r) => !r.skip && r.matched_item_id).length

  // 3개 그룹으로 분류. 원본 인덱스(i)는 유지 — 상태 업데이트 정상 동작.
  const grouped = useMemo(() => {
    const unmatched: { r: Row; i: number }[] = []
    const guessed: { r: Row; i: number }[] = []
    const done: { r: Row; i: number }[] = []
    rows.forEach((r, i) => {
      if (!r.matched_item_id) unmatched.push({ r, i })
      else if (r.matched_via === 'guess') guessed.push({ r, i })
      else done.push({ r, i })
    })
    return { unmatched, guessed, done }
  }, [rows])

  // ── 키보드 워크플로우 ──
  const previewTableRef = useRef<HTMLTableElement>(null)
  const applyBtnRef = useRef<HTMLButtonElement>(null)
  const rowsRef = useRef(rows); rowsRef.current = rows

  // 화면상 행 렌더 순서 (미매칭 → 추정 → 완료).
  const orderedIndices = useMemo(() => [
    ...grouped.unmatched.map(x => x.i),
    ...grouped.guessed.map(x => x.i),
    ...grouped.done.map(x => x.i),
  ], [grouped])

  function focusRowItemPicker(i: number) {
    requestAnimationFrame(() => {
      const btn = previewTableRef.current?.querySelector<HTMLElement>(
        `tr[data-row-idx="${i}"] [data-field="item-trigger"] button`,
      )
      btn?.focus()
    })
  }
  // 다음 미매칭(없으면 다음 추정, 그것도 없으면 저장 버튼) 으로 focus 이동.
  function focusNextTodo(fromIdx: number = -1) {
    requestAnimationFrame(() => {
      const latest = rowsRef.current
      // 우선 미매칭 (matched_item_id 없음)
      for (let i = fromIdx + 1; i < latest.length; i++) {
        if (!latest[i].matched_item_id && !latest[i].skip) return focusRowItemPicker(i)
      }
      for (let i = 0; i < latest.length; i++) {
        if (!latest[i].matched_item_id && !latest[i].skip) return focusRowItemPicker(i)
      }
      // 없으면 추정
      for (let i = 0; i < latest.length; i++) {
        if (latest[i].matched_via === 'guess' && !latest[i].skip) return focusRowItemPicker(i)
      }
      // 다 처리 → 단가 등록 버튼
      applyBtnRef.current?.focus()
    })
  }

  // parse 응답 도착하면 첫 미매칭 행에 자동 focus.
  useEffect(() => {
    if (parsed && rows.length > 0) focusNextTodo(-1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed])

  function rowsKeyDown(e: React.KeyboardEvent) {
    // Ctrl/Cmd + Enter → 단가 등록 (전역, 어느 행에서든)
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      if (effectiveFrom && readyToApply > 0 && !apply.isPending) apply.mutate()
      return
    }
    const el = e.target instanceof HTMLElement ? e.target : null
    if (!el || !previewTableRef.current?.contains(el)) return
    const trEl = el.closest<HTMLElement>('[data-row-idx]')
    const idxStr = trEl?.getAttribute('data-row-idx')
    if (!idxStr) return
    const currentI = parseInt(idxStr, 10)
    const orderPos = orderedIndices.indexOf(currentI)
    if (orderPos < 0) return

    // ItemPicker trigger 버튼일 때만 ↑↓/Del 처리 (input 필드 안이면 native 유지).
    const isPickerBtn = el.tagName === 'BUTTON' && !!el.closest('[data-field="item-trigger"]')
    if (!isPickerBtn) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const next = orderedIndices[orderPos + 1]
      if (next !== undefined) focusRowItemPicker(next)
      else applyBtnRef.current?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prev = orderedIndices[orderPos - 1]
      if (prev !== undefined) focusRowItemPicker(prev)
    } else if (e.key === 'Delete') {
      // 제외(skip) 토글 + 다음 행으로.
      e.preventDefault()
      setRows(cur => cur.map((r, ii) => ii === currentI ? { ...r, skip: !r.skip } : r))
      const next = orderedIndices[orderPos + 1]
      if (next !== undefined) focusRowItemPicker(next)
    }
  }

  function renderRow(r: Row, i: number) {
    const hasMatch = !!r.matched_item_id
    const via = r.matched_via
    const isGuess = via === 'guess'
    const isAuto = via === 'code' || via === 'name'
    return (
      <tr key={i} data-row-idx={i} className={cn(
        'border-t border-slate-100',
        r.skip
          ? 'bg-slate-50/70 text-slate-400'
          : isGuess
            ? 'bg-orange-50/40 hover:bg-orange-50/70'
            : hasMatch
              ? 'hover:bg-emerald-50/40'
              : 'bg-amber-50/40 hover:bg-amber-50/70',
      )}>
        <td className="px-3 py-1.5 font-mono text-xs tabular-nums text-slate-600">{r.source_code || '—'}</td>
        <td className="px-3 py-1.5 text-slate-800">{r.source_name}</td>
        <td className="px-3 py-1.5 text-slate-500 text-xs">{r.unit}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{Number(r.price).toLocaleString()}</td>
        <td className="px-3 py-1.5" data-field="item-trigger">
          <ItemPicker value={r.matched_item_id} onChange={(it) => updateItem(i, it)} placeholder="품목 선택..."
                      initialLabel={r.matched_item_id && r.matched_item_name ? `${r.matched_item_code ?? ''} ${r.matched_item_name}`.trim() : ''} />
        </td>
        <td className="px-3 py-1.5 text-center">
          {r.skip ? (
            <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-500">제외</span>
          ) : hasMatch ? (
            isGuess ? (
              <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-800 inline-flex items-center gap-0.5" title="이름 유사도로 추정 — 확인 후 매핑저장 체크">
                추정
              </span>
            ) : isAuto ? (
              <span className={cn(
                'text-xs px-2 py-0.5 rounded inline-flex items-center gap-0.5',
                r.matched_scope === 'group'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-emerald-100 text-emerald-700',
              )}>
                <CheckCircle2 className="w-3 h-3"/>
                {r.matched_scope === 'group' ? '자동(상위)' : '자동'}
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">수동</span>
            )
          ) : (
            <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">미매칭</span>
          )}
        </td>
        <td className="px-3 py-1.5 text-center">
          <input type="checkbox" checked={r.save_mapping}
                 onChange={() => setRows((cur) => cur.map((rr, ii) => ii === i ? { ...rr, save_mapping: !rr.save_mapping } : rr))}
                 disabled={!hasMatch}
                 className="w-4 h-4 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed"/>
        </td>
        <td className="px-3 py-1.5 text-center">
          <input type="checkbox" checked={r.skip}
                 onChange={() => setRows((cur) => cur.map((rr, ii) => ii === i ? { ...rr, skip: !rr.skip } : rr))}
                 className="w-4 h-4 accent-slate-500 cursor-pointer"/>
        </td>
      </tr>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* 방금 단가 등록 되돌리기 배너 (잘못 올렸을 때 원클릭 복구) */}
      {undoInfo && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5">
          <span className="text-sm text-amber-900">
            방금 단가 <b>{undoInfo.count}건</b> 등록됨 (적용일 {undoInfo.from}). 잘못 올렸다면 되돌릴 수 있어요.
          </span>
          <button onClick={undoLastApply} disabled={undoing}
                  className="ml-auto bg-white hover:bg-rose-50 border border-rose-300 text-rose-600 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50">
            {undoing ? <Loader2 className="w-4 h-4 animate-spin"/> : <RotateCcw className="w-4 h-4"/>}
            방금 등록 되돌리기
          </button>
          <button onClick={() => setUndoInfo(null)} title="배너 닫기"
                  className="text-amber-400 hover:text-amber-700 text-lg leading-none">✕</button>
        </div>
      )}

      {/* 상단: 대상 선택 */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <label className="text-xs text-slate-500 font-medium block mb-1">
          상위 거래처 * <span className="normal-case font-normal text-slate-400">(자식 매출처 전부에 fanout 적용)</span>
        </label>
        <div className="w-96">
          <QuoteTargetPicker
            value={target ? { id: target.id } : null}
            onChange={setTarget}
            placeholder="상위 거래처 선택"
          />
        </div>
      </div>

      {/* 파일 업로드 (드래그앤드롭) */}
      <div
        role="button" tabIndex={canUpload ? 0 : -1}
        onClick={() => canUpload && fileRef.current?.click()}
        onKeyDown={(e) => { if (canUpload && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); fileRef.current?.click() } }}
        className={cn(
          'block rounded-2xl border-2 border-dashed px-8 py-12 text-center bg-white transition-colors focus:outline-none mb-4',
          !canUpload
            ? 'border-slate-200 opacity-50 cursor-not-allowed'
            : dragging
              ? 'border-emerald-500 bg-emerald-50 cursor-pointer'
              : 'border-slate-300 hover:border-emerald-400 focus:border-emerald-500 cursor-pointer',
        )}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files) }}
        onDragOver={(e) => { e.preventDefault(); if (canUpload) setDragging(true) }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false) }}
      >
        <UploadCloud className="mx-auto w-12 h-12 text-slate-400" />
        <p className="mt-3 text-base font-semibold text-slate-700">
          {canUpload ? '여기로 드래그하거나 클릭해서 선택' : '상위 거래처를 먼저 선택하세요'}
        </p>
        <p className="text-sm text-slate-500 mt-1">견적서 파일 (.xls / .xlsx / .xlsb) — 한 번에 한 개만.</p>
        <p className="text-xs text-slate-400 mt-2">
          <strong className="text-slate-500">시트명</strong>에 <code className="bg-slate-100 px-1 rounded">YYYY.MM</code>(또는 <code className="bg-slate-100 px-1 rounded">YYYY년 M월</code>)이 있으면 적용일 자동 설정 <span className="text-slate-300">· 파일명은 보지 않음</span>
        </p>
        <input
          ref={fileRef} type="file"
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
          accept=".xls,.xlsx,.xlsb"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = '' }}
        />
      </div>

      {lastFile && (
        <div className="mb-4 flex flex-wrap gap-2">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg pl-3 pr-2 py-1.5 text-sm shadow-sm">
            <span className="font-medium text-slate-700 truncate max-w-[280px]">{lastFile.name}</span>
            <span className="text-xs text-slate-400 tabular-nums">{(lastFile.size / 1024).toFixed(0)} KB</span>
            <button onClick={() => { setLastFile(null); setParsed(null); setRows([]) }}
                    className="text-slate-400 hover:text-rose-500 p-0.5 rounded" title="선택 해제">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {parsed && (
        <>
          {/* 요약 + 적용일 */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap items-center gap-4">
            <div className="text-sm">
              <span className="mr-3 inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded font-semibold">
                <Building2 className="w-3.5 h-3.5"/>
                {parsed.target.name} → {parsed.target.member_count}개 자식
              </span>
              <span className="text-slate-500">시트</span>{' '}
              <span className="font-semibold">{parsed.sheet}</span>
              <span className="text-slate-400 ml-3">|</span>
              <span className="text-slate-500 ml-3">전체</span>{' '}
              <span className="font-semibold tabular-nums">{parsed.summary.total}</span>
              <span className="text-slate-500 ml-3">매핑</span>{' '}
              <span className="font-semibold text-emerald-700 tabular-nums">{parsed.summary.matched}</span>
              <span className="text-slate-500 ml-3">추정</span>{' '}
              <span className="font-semibold text-orange-600 tabular-nums">{parsed.summary.guessed}</span>
              <span className="text-slate-500 ml-3">미매칭</span>{' '}
              <span className="font-semibold text-amber-700 tabular-nums">{parsed.summary.unmatched}</span>
              {!parsed.auto_detected && !parsed.format_saved && (
                <span className="ml-3 px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">수동 매핑</span>
              )}
              {parsed.format_saved && (
                <span className="ml-3 px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">저장된 포맷 적용됨</span>
              )}
            </div>
            <div className="flex-1"/>
            <button
              onClick={() => setShowMapper((s) => !s)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              <Settings className="w-3.5 h-3.5"/>
              컬럼 매핑
            </button>
            <div className="flex items-center gap-2">
              <div>
                <label className="text-xs text-slate-500 font-medium mr-2">적용 시작일</label>
                <input
                  type="date" value={effectiveFrom} onChange={(e) => setEffFrom(e.target.value)}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <span className="text-slate-400 text-sm">~</span>
              <div>
                <label className="text-xs text-slate-500 font-medium mr-2">종료일<span className="text-slate-400 font-normal">(비우면 무기한)</span></label>
                <input
                  type="date" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
            </div>
            <button
              ref={applyBtnRef}
              disabled={!effectiveFrom || readyToApply === 0 || apply.isPending}
              onClick={() => apply.mutate()}
              className={cn(
                'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg focus:ring-2 focus:ring-emerald-300 focus:outline-none',
                (!effectiveFrom || readyToApply === 0 || apply.isPending)
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              )}
            >
              <Save className="w-4 h-4"/>
              단가 등록 ({readyToApply}건)
              {parsed.target.member_count && (
                <span className="text-emerald-100 text-xs">× {parsed.target.member_count} 자식</span>
              )}
            </button>
          </div>

          {/* 컬럼 매핑 편집기 */}
          {showMapper && (
            <div className="bg-white rounded-xl border border-blue-200 bg-blue-50/30 p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-slate-800">
                  컬럼 매핑 편집
                  <span className="ml-2 text-xs text-slate-500 font-normal">
                    이 상위 거래처에 포맷 저장
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => saveFormat.mutate()}
                    disabled={!editColMap.name || editColMap.price === null}
                    className="text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg"
                  >
                    포맷 저장
                  </button>
                  <button
                    onClick={reparse}
                    className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg"
                  >
                    이 설정으로 재파싱
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <div>
                  <label className="text-xs text-slate-500 block mb-0.5">시트 힌트</label>
                  <input
                    value={editSheet} onChange={(e) => setEditSheet(e.target.value)}
                    className="px-2 py-1 border border-slate-300 rounded text-sm w-40"
                    placeholder="빈값=첫 시트"
                  />
                  {parsed.all_sheet_names.length > 1 && (
                    <div className="text-[10px] text-slate-400 mt-0.5">시트: {parsed.all_sheet_names.join(', ')}</div>
                  )}
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-0.5">헤더 행 (0-based)</label>
                  <input
                    type="number" min={0}
                    value={editHeaderRow} onChange={(e) => setEditHeaderRow(Number(e.target.value) || 0)}
                    className="px-2 py-1 border border-slate-300 rounded text-sm w-24 tabular-nums"
                  />
                </div>
              </div>

              {parsed.sheet_headers.length > 0 && (
                <div className="mb-3 overflow-x-auto">
                  <table className="text-xs border border-slate-200">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="px-2 py-1 border-b border-r border-slate-200">행</th>
                        {parsed.sheet_headers[0]?.map((_, i) => (
                          <th key={i} className="px-2 py-1 border-b border-r border-slate-200 min-w-[80px]">
                            col {i}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.sheet_headers.slice(0, 8).map((row, ri) => (
                        <tr key={ri} className={cn(ri === editHeaderRow && 'bg-blue-100/60 font-semibold')}>
                          <td className="px-2 py-1 border-b border-r border-slate-200 tabular-nums text-slate-500">{ri}</td>
                          {row.map((c, ci) => (
                            <td key={ci} className="px-2 py-1 border-b border-r border-slate-200 text-slate-700 max-w-[200px] truncate">
                              {c || '·'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="grid grid-cols-5 gap-3">
                {(['code', 'name', 'spec', 'unit', 'price'] as ColKey[]).map((k) => (
                  <div key={k}>
                    <label className="text-xs text-slate-500 block mb-0.5">
                      {COL_LABELS[k]}
                      {(k === 'name' || k === 'price') && <span className="text-rose-500">*</span>}
                    </label>
                    <select
                      value={editColMap[k] === null ? '' : String(editColMap[k])}
                      onChange={(e) => setEditColMap({
                        ...editColMap,
                        [k]: e.target.value === '' ? null : Number(e.target.value),
                      })}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                    >
                      <option value="">— 없음 —</option>
                      {parsed.sheet_headers[0]?.map((_, i) => (
                        <option key={i} value={i}>col {i} ({(parsed.sheet_headers[editHeaderRow] || [])[i] || '(비어있음)'})</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-3 py-1.5 text-[11px] text-slate-500 border-b border-slate-100 bg-slate-50/60">
              단축키: <kbd className="px-1 py-0.5 bg-white border border-slate-300 rounded font-mono">↑↓</kbd> 행 이동 ·
              <kbd className="px-1 py-0.5 bg-white border border-slate-300 rounded font-mono ml-1">Enter/Space</kbd> 품목 선택기 열기 ·
              선택 후 <span className="font-semibold">자동으로 다음 미매칭 행</span>으로 이동 ·
              <kbd className="px-1 py-0.5 bg-white border border-slate-300 rounded font-mono ml-1">Del</kbd> 제외 토글 ·
              <kbd className="px-1 py-0.5 bg-white border border-slate-300 rounded font-mono ml-1">Ctrl+Enter</kbd> 단가 등록
            </div>
            <table ref={previewTableRef} onKeyDown={rowsKeyDown} className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
                <tr>
                  <th className="px-3 py-2.5 text-left font-medium w-24">원본코드</th>
                  <th className="px-3 py-2.5 text-left font-medium">원본품명</th>
                  <th className="px-3 py-2.5 text-left font-medium w-16">단위</th>
                  <th className="px-3 py-2.5 text-right font-medium w-24">단가</th>
                  <th className="px-3 py-2.5 text-left font-medium w-72">매핑된 품목</th>
                  <th className="px-3 py-2.5 text-center font-medium w-24">상태</th>
                  <th className="px-3 py-2.5 text-center font-medium w-16">매핑저장</th>
                  <th className="px-3 py-2.5 text-center font-medium w-16">제외</th>
                </tr>
              </thead>
              <tbody>
                {grouped.unmatched.length > 0 && (
                  <>
                    <tr className="bg-amber-100/70 border-t-2 border-amber-300">
                      <td colSpan={8} className="px-4 py-2.5">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5"/>
                          <div>
                            <div className="text-sm font-bold text-amber-900">
                              1. 미매칭 {grouped.unmatched.length}건 — 사용자가 직접 품목을 선택해야 합니다
                            </div>
                            <div className="text-xs text-amber-800/90 mt-0.5">
                              각 줄의 <span className="font-semibold">'품목 선택...'</span> 버튼을 눌러 매칭할 품목을 찾으세요.
                              선택하면 '매핑저장'이 자동 체크되어 다음 견적서부터 자동 매칭됩니다.
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {grouped.unmatched.map(({ r, i }) => renderRow(r, i))}
                  </>
                )}

                {grouped.guessed.length > 0 && (
                  <>
                    <tr className="bg-orange-100/70 border-t-2 border-orange-300">
                      <td colSpan={8} className="px-4 py-2.5">
                        <div className="flex items-start gap-2">
                          <HelpCircle className="w-5 h-5 text-orange-700 shrink-0 mt-0.5"/>
                          <div>
                            <div className="text-sm font-bold text-orange-900">
                              2. 추정 {grouped.guessed.length}건 — 자동 매칭 결과가 맞는지 확인해주세요
                            </div>
                            <div className="text-xs text-orange-800/90 mt-0.5">
                              품명 유사도로 추정된 매칭입니다.
                              <span className="font-semibold"> 맞으면 그대로 두시고</span>,
                              <span className="font-semibold"> 틀리면 '품목 선택...'</span>을 눌러 다시 선택하거나
                              <span className="font-semibold"> '제외' 체크</span>하세요.
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {grouped.guessed.map(({ r, i }) => renderRow(r, i))}
                  </>
                )}

                {grouped.done.length > 0 && (
                  <>
                    <tr className="bg-emerald-100/70 border-t-2 border-emerald-300">
                      <td colSpan={8} className="px-4 py-2.5">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5"/>
                          <div>
                            <div className="text-sm font-bold text-emerald-900">
                              3. 매칭 완료 {grouped.done.length}건 — 특별한 작업 불필요
                            </div>
                            <div className="text-xs text-emerald-800/90 mt-0.5">
                              이전에 등록된 매핑으로 자동 매칭된 항목입니다. 그대로 두시면 됩니다.
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {grouped.done.map(({ r, i }) => renderRow(r, i))}
                  </>
                )}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-sm text-amber-700 bg-amber-50/50">
                      파싱된 라인이 없습니다. 상단 "컬럼 매핑"에서 헤더 행/컬럼을 지정하고 "재파싱"하세요.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

    </div>
  )
}
